<template>
  <div class="subject-table">
    <table class="do-table" v-for="klassCal in filteredKlassCals" :key="klassCal.id">
      <thead>
        <tr>
          <th v-if="isEditingDates" class="subject-table__th subject-table__th--select"></th>
          <th class="subject-table__th subject-table__th--name">{{ klassCal.name }}</th>
          <th class="subject-table__th subject-table__th--type">Type</th>
          <th class="subject-table__th subject-table__th--teachers">Personale</th>
          <th v-if="showDates" class="subject-table__th subject-table__th--date">
            Startdato
            <span class="icon-wrench subject-table__wrench"
                  content="Rediger datoer på flere fag"
                  v-tippy="{ arrow: true, placement: 'top' }"
                  @click.stop="$emit('toggleEditDates')"></span>
          </th>
          <th v-if="showDates" class="subject-table__th subject-table__th--date">
            Slutdato
            <span class="icon-wrench subject-table__wrench"
                  content="Rediger datoer på flere fag"
                  v-tippy="{ arrow: true, placement: 'top' }"
                  @click.stop="$emit('toggleEditDates')"></span>
          </th>
          <th v-if="showSubjectsRequest" class="subject-table__th subject-table__th--requested">Ønsket af</th>
          <th class="subject-table__th subject-table__th--rooms">Lokaler</th>
          <th class="subject-table__th subject-table__th--klasses">Klasser</th>
          <th class="subject-table__th subject-table__th--lessons">Lek. pr. uge: {{ totalLessons(klassCal.vSubjects) }}</th>
          <th class="subject-table__th subject-table__th--hours">Timer: {{ totalHours(klassCal.vSubjects, klassCal.id) }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="klassCal.vSubjects.length === 0">
          <td :colspan="7 + (showSubjectsRequest ? 1 : 0) + (showDates ? 2 : 0) + (isEditingDates ? 1 : 0)" class="subject-table__empty">Ingen fag</td>
        </tr>
        <tr v-for="subject in sortedSubjects(klassCal.vSubjects)"
            :key="subject.id"
            class="subject-table__row"
            :class="{ 'subject-table__row--selected': isEditingDates && selectedSubjectIds.indexOf(subject.id) >= 0 }"
            @click="onRowClick(subject, klassCal.id)">
          <td v-if="isEditingDates"
              class="subject-table__td subject-table__td--select"
              @click.stop="$emit('toggleSubjectSelection', subject.id)">
            <input type="checkbox"
                   :checked="selectedSubjectIds.indexOf(subject.id) >= 0">
          </td>
          <td class="subject-table__td subject-table__td--name">
            <span class="subject-table__color-dot" :style="{ background: subject.color }"></span>
            {{ subject.name }}
          </td>
          <td class="subject-table__td subject-table__td--type">
            {{ subjectTypeLabel(subject.subject_type) }}
          </td>
          <td class="subject-table__td subject-table__td--teachers">
            <template v-if="showTeacherPoolHours && subject.vTeachersWithPool && subject.vTeachersWithPool.length">
              <span v-for="(teacher, teacherIdx) in subject.vTeachersWithPool"
                    :key="teacherIdx"
                    class="subject-table__teacher">
                <template v-if="teacherIdx > 0">, </template>{{ teacher.name }}<span v-if="teacher.hours != null" class="subject-table__teacher-pool"> ({{ formatTeacherPoolHours(teacher.hours, subject) }})</span>
              </span>
            </template>
            <template v-else>
              {{ subject.vUserNames && subject.vUserNames.length ? subject.vUserNames.join(', ') : '' }}
            </template>
          </td>
          <td v-if="showDates" class="subject-table__td subject-table__td--date">
            {{ formatDate(subject.from_date) }}
          </td>
          <td v-if="showDates" class="subject-table__td subject-table__td--date">
            {{ formatDate(subject.to_date) }}
          </td>
          <td v-if="showSubjectsRequest" class="subject-table__td subject-table__td--requested">
            {{ subject.vRequestedBy && subject.vRequestedBy.length ? subject.vRequestedBy.join(', ') : '' }}
          </td>
          <td class="subject-table__td subject-table__td--rooms">
            {{ getRoomNames(subject) }}
          </td>
          <td class="subject-table__td subject-table__td--klasses">
            {{ subject.v_subjectOtherKlasses && subject.v_subjectOtherKlasses.length ? subject.v_subjectOtherKlasses.join(', ') : '' }}
          </td>
          <td class="subject-table__td subject-table__td--lessons">
            <span v-if="isExcludedFromLessons(subject)"
                  class="subject-table__excluded-icon"
                  content="Tæller ikke med i totalen"
                  v-tippy="{ arrow: false, placement: 'left' }">ⓘ</span>
            {{ subject.vLessonsPerWeek }}
          </td>
          <td class="subject-table__td subject-table__td--hours">
            <span v-if="isExcludedFromLessons(subject)"
                  class="subject-table__excluded-icon"
                  content="Tæller ikke med i totalen"
                  v-tippy="{ arrow: false, placement: 'left' }">ⓘ</span>
            {{ getHoursForKlass(subject, klassCal.id) }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'SubjectsDistributionTable',
  props: {
    subjectDefaultDuration: {
      type: Number,
      default: 45
    },
    filteredKlassCals: {
      type: Array,
      default: () => []
    },
    doesRessCountInKlassTotalHours: {
      type: Boolean,
      default: true
    },
    showLessonsPerWeek: {
      type: Boolean,
      default: false
    },
    showSubjectsRequest: {
      type: Boolean,
      default: false
    },
    showDates: {
      type: Boolean,
      default: false
    },
    showTeacherPoolHours: {
      type: Boolean,
      default: false
    },
    isEditingDates: {
      type: Boolean,
      default: false
    },
    selectedSubjectIds: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    school () {
      return this.$store.state.activeSchool
    },
    poolSubjectTypeLabel () {
      const title = this.$store.getters.localPoolTitle
      return title || 'Lokalpulje'
    }
  },
  methods: {
    isExcludedType (subject) {
      if (this.doesRessCountInKlassTotalHours) return false
      const excludedTypes = ['resource', 'person_resource_visible']
      return excludedTypes.indexOf(subject.subject_type) >= 0
    },
    isExcludedFromLessons (subject) {
      if (this.doesRessCountInKlassTotalHours) return false
      const teachingTypes = ['subject']
      return teachingTypes.indexOf(subject.subject_type) < 0
    },
    totalLessons (subjects) {
      return subjects.reduce((sum, s) => {
        if (this.isExcludedFromLessons(s)) return sum
        return sum + parseFloat(s.vLessonsPerWeek || 0)
      }, 0).toFixed(1)
    },
    totalHours (subjects, klassCalId) {
      return subjects.reduce((sum, s) => {
        if (this.isExcludedFromLessons(s)) return sum
        const hours = this.getHoursForKlass(s, klassCalId)
        return sum + (isNaN(hours) ? 0 : parseFloat(hours))
      }, 0).toFixed(1)
    },
    getHoursForKlass (subject, klassCalId) {
      if (subject.subject_calendars && subject.subject_calendars.length) {
        const match = subject.subject_calendars.find(sc => sc.calendar_id === klassCalId)
        if (match && match.hours != null) return parseFloat(match.hours)
      }
      return subject.vHours
    },
    sortedSubjects (subjects) {
      return [...subjects].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'da'))
    },
    subjectTypeLabel (type) {
      const labels = {
        subject: 'Fag',
        activity: 'Aktivitet',
        available: 'Rådighedstime',
        person_resource: 'Ressource',
        person_resource_visible: 'Ressource (synlig)',
        resource: 'Ressource',
        additional_tasks: 'Øvrige opgaver',
        preparation: 'Forberedelse',
        pool: this.poolSubjectTypeLabel,
        club: 'SFO',
        without_kids: 'Uden børn',
        frame: 'Temavisning',
        optional: 'Valgfag/Hold',
        supplemental: 'UUV',
        break: 'Pause'
      }
      return labels[type] || type || ''
    },
    getRoomNames (subject) {
      if (!subject.cals || !subject.cals.length) return ''
      return subject.cals
        .filter(cal => cal.calendar_type === 'room')
        .map(cal => cal.name)
        .join(', ')
    },
    formatDate (dateStr) {
      if (!dateStr) return ''
      return moment(dateStr).format('D. MMM YYYY')
    },
    formatTeacherPoolHours (hours, subject) {
      const parsed = parseFloat(hours)
      if (isNaN(parsed)) return ''
      if (this.showLessonsPerWeek) {
        const duration = (subject && subject.default_duration) || this.subjectDefaultDuration
        return this.$helpers.calcLessonsPerWeek(parsed, duration)
      }
      return Math.round(parsed * 10) / 10
    },
    onRowClick (subject, klassCalId) {
      if (this.isEditingDates) {
        this.$emit('toggleSubjectSelection', subject.id)
      } else {
        this.$emit('editSubject', subject, klassCalId)
      }
    }
  }
}
</script>

<style scoped>
.subject-table {
  border: 1px solid #e2e8f0;
  margin-bottom: 1rem;
}

.subject-table .do-table {
  border: none;
}

.subject-table .do-table thead {
  border-top: none;
  border-bottom: 1px solid #e2e8f0;
}

.subject-table .do-table tbody tr {
  cursor: pointer;
}

.subject-table .do-table tbody tr:hover {
  background-color: aliceblue;
}

.subject-table__th--name {
  color: #09090b;
  font-weight: 700;
  font-size: 14px;
}

.subject-table__td--type {
  font-size: 12px;
  color: #697386;
  white-space: nowrap;
}

.subject-table__td--teachers {
  font-size: 12px;
}

.subject-table__teacher-pool {
  color: #697386;
  font-variant-numeric: tabular-nums;
}

.subject-table__td--requested {
  font-size: 12px;
  color: #697386;
}

.subject-table__td--date {
  font-size: 12px;
  color: #697386;
  white-space: nowrap;
}

.subject-table__th--lessons,
.subject-table__th--hours {
  text-align: right;
}

.subject-table__td--lessons,
.subject-table__td--hours {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.subject-table__row td {
  border-left: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
}

.subject-table__excluded-icon {
  font-size: 11px;
  color: #a0aec0;
  margin-left: 2px;
  cursor: help;
}

.subject-table__wrench {
  margin-left: 6px;
  font-size: 12px;
  color: #a0aec0;
  cursor: pointer;
}

.subject-table__wrench:hover {
  color: #0066cc;
}

.subject-table__th--select,
.subject-table__td--select {
  width: 32px;
  text-align: center;
}

.subject-table__td--select {
  cursor: pointer;
}

.subject-table__td--select input[type="checkbox"] {
  cursor: pointer;
  pointer-events: none;
}

.subject-table__row--selected {
  background-color: #eef6ff;
}

.subject-table .do-table tbody tr.subject-table__row--selected:hover {
  background-color: #e2efff;
}
</style>



// WEBPACK FOOTER //
// SubjectsDistributionTable.vue?241db532